import { useMemo, useRef, useState } from "react";
import type { ConnectorImportResult, HealthDataConnector } from "../healthDataConnector";
import type { PreferredUnits } from "../../athlete-profile/types/athleteProfile";
import type { AppleHealthImportProgress, DailyHealthRecord, HealthImportDiagnostics, HealthImportSession } from "../dailyHealthTypes";
import { buildDailyMetricTrends } from "../dailyHealthTrends";
import { downloadAppleHealthSampleXml } from "../appleHealthSampleDownload";
import { formatMinutesAsDuration, formatWeightFromKilograms } from "../../../lib/unitPreferences";

type DailyHealthPanelProps = {
  connector: HealthDataConnector;
  records: DailyHealthRecord[];
  sessions: HealthImportSession[];
  diagnostics: HealthImportDiagnostics | null;
  onImportCompleted: (result: ConnectorImportResult) => void;
  onImportError: (message: string) => void;
  selectedDate: string | null;
  onSelectedDateChange: (localDate: string) => void;
  preferredUnits?: PreferredUnits;
};

const OVERVIEW_METRICS: Array<{ key: string; label: string; read: (record: DailyHealthRecord) => number | null; unit?: string }> = [
  { key: "bodyWeightKg", label: "Body weight", read: record => record.bodyComposition.bodyWeightKg, unit: "kg" },
  { key: "bodyFatPercentage", label: "Body fat", read: record => record.bodyComposition.bodyFatPercentage, unit: "%" },
  { key: "leanBodyMassKg", label: "Lean mass", read: record => record.bodyComposition.leanBodyMassKg, unit: "kg" },
  { key: "hrvSdnnMs", label: "HRV SDNN", read: record => record.recovery.hrvSdnnMs, unit: "ms" },
  { key: "restingHeartRateBpm", label: "Resting HR", read: record => record.recovery.restingHeartRateBpm, unit: "bpm" },
  { key: "sleepDurationMinutes", label: "Sleep duration", read: record => record.sleep.totalSleepMinutes, unit: "min" },
  { key: "respiratoryRateBpm", label: "Respiratory rate", read: record => record.recovery.respiratoryRateBpm, unit: "br/min" },
  { key: "vo2MaxMlKgMin", label: "VO2 max", read: record => record.cardiovascular.vo2MaxMlKgMin, unit: "ml/kg/min" },
  { key: "steps", label: "Steps", read: record => record.activityContext.steps },
  { key: "activeEnergyKcal", label: "Active energy", read: record => record.activityContext.activeEnergyKcal, unit: "kcal" }
];

function formatNumber(value: number | null, unit?: string): string {
  if (value === null) return "Not available";
  const fixed = Math.abs(value) >= 100 ? value.toFixed(0) : value.toFixed(2);
  return unit ? `${fixed} ${unit}` : fixed;
}

function formatOverviewMetric(metricKey: string, value: number | null, preferredUnits: PreferredUnits): string {
  if (value === null) return "Not available";
  if (metricKey === "bodyWeightKg" || metricKey === "leanBodyMassKg") {
    return formatWeightFromKilograms(value, preferredUnits);
  }
  if (metricKey === "sleepDurationMinutes") {
    return formatMinutesAsDuration(value);
  }
  return formatNumber(value, OVERVIEW_METRICS.find(metric => metric.key === metricKey)?.unit);
}

function formatDateLabel(localDate: string): string {
  const date = new Date(`${localDate}T12:00:00`);
  return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric" });
}

export function DailyHealthPanel({
  connector,
  records,
  sessions,
  diagnostics,
  onImportCompleted,
  onImportError,
  selectedDate,
  onSelectedDateChange,
  preferredUnits = "imperial"
}: DailyHealthPanelProps) {
  const [importProgress, setImportProgress] = useState<AppleHealthImportProgress | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  const [duplicateWarning, setDuplicateWarning] = useState<string | null>(null);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [preferredStepsSource, setPreferredStepsSource] = useState("");
  const [preferredSleepSource, setPreferredSleepSource] = useState("");
  const abortRef = useRef<AbortController | null>(null);
  const isDevelopmentMode = import.meta.env.DEV;

  const sortedRecords = useMemo(
    () => [...records].sort((left, right) => right.localDate.localeCompare(left.localDate)),
    [records]
  );

  const effectiveDate = selectedDate ?? sortedRecords[0]?.localDate ?? null;
  const selectedRecord = sortedRecords.find(record => record.localDate === effectiveDate) ?? null;

  const availableOverview = selectedRecord
    ? OVERVIEW_METRICS.filter(metric => metric.read(selectedRecord) !== null)
    : [];
  const unavailableOverview = selectedRecord
    ? OVERVIEW_METRICS.filter(metric => metric.read(selectedRecord) === null).map(metric => metric.label)
    : [];

  const latestSession = sessions[0] ?? null;

  const trends = useMemo(() => {
    if (sortedRecords.length === 0) return [];
    return [
      ...buildDailyMetricTrends(sortedRecords, "bodyWeightKg"),
      ...buildDailyMetricTrends(sortedRecords, "hrvSdnnMs"),
      ...buildDailyMetricTrends(sortedRecords, "restingHeartRateBpm"),
      ...buildDailyMetricTrends(sortedRecords, "sleepDurationMinutes"),
      ...buildDailyMetricTrends(sortedRecords, "steps")
    ];
  }, [sortedRecords]);

  async function runImport(file: File, allowReimport: boolean): Promise<void> {
    setIsImporting(true);
    setImportError(null);
    setDuplicateWarning(null);
    setImportProgress({ phase: "fingerprint", percent: 0, recordsScanned: 0, supportedRecords: 0, ignoredRecords: 0 });

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const result = await connector.import(file, {
        signal: controller.signal,
        allowReimport,
        sourcePreferences: {
          steps: preferredStepsSource || undefined,
          sleep: preferredSleepSource || undefined
        },
        onProgress: progress => {
          setImportProgress({
            ...progress,
            recordsScanned: Math.max(progress.recordsScanned, importProgress?.recordsScanned ?? 0),
            supportedRecords: Math.max(progress.supportedRecords, importProgress?.supportedRecords ?? 0),
            ignoredRecords: Math.max(progress.ignoredRecords, importProgress?.ignoredRecords ?? 0)
          });
        }
      });

      if (result.duplicateImportDetected && result.session.status === "cancelled") {
        setDuplicateWarning("This file fingerprint was already imported. You can cancel or run a deliberate re-import.");
        setPendingFile(file);
      } else {
        setPendingFile(null);
      }

      onImportCompleted(result);
      setImportProgress({ phase: "complete", percent: 100, recordsScanned: result.session.recordsScanned, supportedRecords: result.session.recordsSupported, ignoredRecords: result.session.recordsIgnored });
    } catch (error) {
      const message = error instanceof Error ? error.message : "Import failed.";
      setImportError(message);
      onImportError(message);
    } finally {
      abortRef.current = null;
      setIsImporting(false);
    }
  }

  function cancelImport(): void {
    abortRef.current?.abort();
  }

  return (
    <section className="daily-health-panel" aria-labelledby="daily-health-heading">
      <div className="daily-health-header">
        <div>
          <p className="eyebrow">DAILY HEALTH</p>
          <h2 id="daily-health-heading">Daily Health Metrics</h2>
          <p className="command-note">Apple Health data is imported locally in this browser. Live HealthKit synchronization will require a future iPhone companion app.</p>
          <p className="command-note">This is not a medical-record system. Values are informational and may differ by source app.</p>
        </div>
      </div>

      <article className="daily-health-import-card">
        <h3>Apple Health Import</h3>
        <p className="command-note">Select Apple Health export.xml. ZIP import is not yet available in this browser build.</p>
        <p className="command-note">Data stays in this browser storage. Clearing browser storage may remove imported data.</p>

        <div className="daily-health-import-actions">
          <label className="ride-analysis-button primary-action" role="button">
            Select export.xml
            <input
              type="file"
              accept=".xml,text/xml,application/xml"
              onChange={event => {
                const file = event.target.files?.[0];
                if (!file) return;
                void runImport(file, false);
                event.target.value = "";
              }}
              disabled={isImporting}
            />
          </label>
          {isDevelopmentMode ? (
            <button
              type="button"
              className="ride-analysis-button secondary-action"
              onClick={() => downloadAppleHealthSampleXml()}
              disabled={isImporting}
            >
              Download Sample Apple Health XML (Development Sample Data)
            </button>
          ) : null}
          <button type="button" className="ride-analysis-button secondary-action" onClick={cancelImport} disabled={!isImporting}>Cancel</button>
        </div>

        <div className="daily-health-preferences">
          <label>Preferred steps source (optional)
            <input value={preferredStepsSource} onChange={event => setPreferredStepsSource(event.target.value)} placeholder="Apple Watch" />
          </label>
          <label>Preferred sleep source (optional)
            <input value={preferredSleepSource} onChange={event => setPreferredSleepSource(event.target.value)} placeholder="AutoSleep" />
          </label>
        </div>

        {importProgress ? (
          <div className="upload-progress" aria-live="polite">
            <div className="upload-progress-heading">
              <strong>Import {importProgress.phase}</strong>
              <span>{importProgress.percent}%</span>
            </div>
            <progress value={importProgress.percent} max={100} />
            <p className="command-note">Scanned {importProgress.recordsScanned} records · Supported {importProgress.supportedRecords} · Ignored {importProgress.ignoredRecords}</p>
          </div>
        ) : null}

        {duplicateWarning ? (
          <div className="daily-health-warning" role="status">
            <p>{duplicateWarning}</p>
            <div className="daily-health-import-actions">
              <button
                type="button"
                className="ride-analysis-button secondary-action"
                onClick={() => {
                  if (!pendingFile) return;
                  void runImport(pendingFile, true);
                }}
                disabled={!pendingFile || isImporting}
              >
                Re-import anyway
              </button>
              <button type="button" className="ride-analysis-button secondary-action" onClick={() => setPendingFile(null)}>Dismiss</button>
            </div>
          </div>
        ) : null}

        {importError ? <p className="timeline-submit-error" role="alert">{importError}</p> : null}
        {latestSession ? (
          <p className="command-note">
            Last import: {new Date(latestSession.startedAt).toLocaleString()} · {latestSession.status} · Date range {latestSession.dateRangeStart ?? "--"} to {latestSession.dateRangeEnd ?? "--"}
          </p>
        ) : null}
      </article>

      {sortedRecords.length > 0 ? (
        <article className="daily-health-overview">
          <div className="daily-health-overview-header">
            <h3>Daily Health Overview</h3>
            <label>Date
              <select value={effectiveDate ?? ""} onChange={event => onSelectedDateChange(event.target.value)}>
                {sortedRecords.map(record => (
                  <option key={record.localDate} value={record.localDate}>{formatDateLabel(record.localDate)}</option>
                ))}
              </select>
            </label>
          </div>

          {selectedRecord ? (
            <>
              <div className="daily-health-metric-grid">
                {availableOverview.map(metric => (
                  <article key={metric.key} className="summary-item">
                    <span>{metric.label}</span>
                    <strong>{formatOverviewMetric(metric.key, metric.read(selectedRecord), preferredUnits)}</strong>
                  </article>
                ))}
              </div>

              {selectedRecord.sleep.totalSleepMinutes !== null ? (
                <div className="daily-health-sleep-breakdown">
                  <p className="command-note">
                    Sleep stages: Core {selectedRecord.sleep.coreSleepMinutes ?? 0} min · Deep {selectedRecord.sleep.deepSleepMinutes ?? 0} min · REM {selectedRecord.sleep.remSleepMinutes ?? 0} min · Awake {selectedRecord.sleep.awakeMinutes ?? 0} min
                  </p>
                </div>
              ) : null}

              {unavailableOverview.length > 0 ? (
                <p className="command-note">Not available from this import: {unavailableOverview.join(", ")}</p>
              ) : null}

              <details className="upload-diagnostics">
                <summary>Data provenance and quality</summary>
                <dl>
                  <div><dt>Local date</dt><dd>{selectedRecord.localDate}</dd></div>
                  <div><dt>Timezone</dt><dd>{selectedRecord.timezone}</dd></div>
                  <div><dt>Sources</dt><dd>{selectedRecord.sourceSummaries.map(source => `${source.name} (${source.recordCount})`).join(", ") || "None"}</dd></div>
                  <div><dt>Metric coverage</dt><dd>{selectedRecord.dataQuality.metricCoverage.join(", ") || "None"}</dd></div>
                  <div><dt>Confidence</dt><dd>{selectedRecord.dataQuality.confidence}</dd></div>
                  <div><dt>Conflicts</dt><dd>{selectedRecord.dataQuality.conflicts.length}</dd></div>
                </dl>
                <ul className="command-list compact-list">
                  {selectedRecord.dataQuality.conflicts.map(conflict => (
                    <li key={`${conflict.metric}:${conflict.reason}`}>{conflict.metric}: {conflict.reason} ({conflict.sources.join(", ")})</li>
                  ))}
                </ul>
                <dl>
                  {Object.entries(selectedRecord.metricProvenance).map(([metric, entry]) => (
                    <div key={metric}><dt>{metric}</dt><dd>{entry?.sourceName ?? "Unknown"} · {entry?.originalValue ?? "--"} {entry?.originalUnit ?? ""} · normalized {entry?.normalizedValue ?? "--"} {entry?.normalizedUnit ?? ""}</dd></div>
                  ))}
                </dl>
              </details>
            </>
          ) : null}
        </article>
      ) : (
        <p className="command-note">No daily health records imported yet.</p>
      )}

      <article className="daily-health-overview">
        <h3>Daily Trend Foundation</h3>
        <p className="command-note">Trend language is conservative and non-diagnostic.</p>
        <div className="daily-health-trend-grid">
          {trends.map(trend => (
            <article key={`${trend.metric}-${trend.windowDays}`} className="summary-item">
              <span>{trend.metric} · {trend.windowDays}d</span>
              <strong>{trend.language}</strong>
              <small>Samples {trend.sampleCount} · Mean {trend.mean === null ? "--" : trend.mean.toFixed(2)} · Change {trend.percentChange === null ? "--" : `${trend.percentChange.toFixed(1)}%`}</small>
            </article>
          ))}
        </div>
      </article>

      <details className="upload-diagnostics">
        <summary>Developer diagnostics</summary>
        <dl>
          <div><dt>Adapter version</dt><dd>{diagnostics?.adapterVersion ?? "--"}</dd></div>
          <div><dt>Parser mode</dt><dd>{diagnostics?.parserMode ?? "--"}</dd></div>
          <div><dt>Import fingerprint</dt><dd>{diagnostics?.importFingerprint ?? "--"}</dd></div>
          <div><dt>Records scanned</dt><dd>{diagnostics?.recordsScanned ?? 0}</dd></div>
          <div><dt>Supported records</dt><dd>{diagnostics?.supportedRecords ?? 0}</dd></div>
          <div><dt>Ignored records</dt><dd>{diagnostics?.ignoredRecords ?? 0}</dd></div>
          <div><dt>Duplicate records</dt><dd>{diagnostics?.duplicateRecords ?? 0}</dd></div>
          <div><dt>Date range</dt><dd>{diagnostics?.dateRangeStart ?? "--"} to {diagnostics?.dateRangeEnd ?? "--"}</dd></div>
          <div><dt>Source names detected</dt><dd>{diagnostics?.sourceNamesDetected.join(", ") || "--"}</dd></div>
          <div><dt>Normalized units</dt><dd>{diagnostics?.normalizedUnits.join(", ") || "--"}</dd></div>
          <div><dt>Source resolution decisions</dt><dd>{diagnostics?.sourceResolutionDecisions.length ?? 0}</dd></div>
          <div><dt>Daily records created</dt><dd>{diagnostics?.dailyRecordsCreated ?? 0}</dd></div>
          <div><dt>Daily records updated</dt><dd>{diagnostics?.dailyRecordsUpdated ?? 0}</dd></div>
          <div><dt>Timeline events created</dt><dd>{diagnostics?.timelineEventsCreated ?? 0}</dd></div>
          <div><dt>Persistence save result</dt><dd>{diagnostics?.persistenceSaveResult ?? "--"}</dd></div>
          <div><dt>Restore result</dt><dd>{diagnostics?.restoreResult ?? "--"}</dd></div>
          <div><dt>Parsing duration</dt><dd>{diagnostics?.parsingDurationMs ?? 0} ms</dd></div>
          <div><dt>Peak memory estimate</dt><dd>{diagnostics?.peakMemoryEstimateBytes ?? "N/A"}</dd></div>
          <div><dt>Warnings</dt><dd>{diagnostics?.warnings.join(" | ") || "None"}</dd></div>
          <div><dt>Errors</dt><dd>{diagnostics?.errors.join(" | ") || "None"}</dd></div>
          <div><dt>Live sync status</dt><dd>Not yet available</dd></div>
        </dl>
      </details>
    </section>
  );
}
